/**
 * Workplace @user / @team / #channel reference helpers (ID-backed Markdown links).
 */

export function userReferenceHref(orgId, userId) {
  if (orgId == null || userId == null) return null;
  return `/o/${encodeURIComponent(String(orgId))}/members/${encodeURIComponent(String(userId))}`;
}

export function channelReferenceHref(orgId, conversationId) {
  if (orgId == null || conversationId == null) return null;
  return `/o/${encodeURIComponent(String(orgId))}/messages/${encodeURIComponent(String(conversationId))}`;
}

export function teamReferenceHref(orgId, teamId) {
  if (orgId == null || teamId == null) return null;
  return `/o/${encodeURIComponent(String(orgId))}/teams/${encodeURIComponent(String(teamId))}`;
}

/** App route used when navigating from a rendered team mention. */
export function teamReferenceRoute(orgId, teamId) {
  if (orgId == null || teamId == null) return null;
  return `/teams/${encodeURIComponent(String(teamId))}?org_id=${encodeURIComponent(String(orgId))}`;
}

export function userReferenceMarkdown(orgId, userId, displayName) {
  const label = String(displayName || "User").replace(/[[\]]/g, "");
  return `[@${label}](${userReferenceHref(orgId, userId)})`;
}

/** Social-plane @mention link: /u/:handle */
export function socialUserReferenceHref(username) {
  if (!username) return null;
  return `/u/${encodeURIComponent(String(username))}`;
}

export function socialUserReferenceMarkdown(displayName, username) {
  const label = String(displayName || username || "User").replace(
    /[[\]]/g,
    ""
  );
  return `[@${label}](${socialUserReferenceHref(username)})`;
}

export function channelReferenceMarkdown(orgId, conversationId, channelName) {
  const label = String(channelName || "channel")
    .replace(/^#/, "")
    .replace(/[[\]]/g, "");
  return `[#${label}](${channelReferenceHref(orgId, conversationId)})`;
}

export function teamReferenceMarkdown(orgId, teamId, teamName) {
  const label = String(teamName || "Team").replace(/[[\]]/g, "");
  return `[@${label}](${teamReferenceHref(orgId, teamId)})`;
}

/** Human UUID members or Circatron bot principals (`cbot_<id>`). */
const USER_HREF = /^\/o\/([^/]+)\/members\/([0-9a-fA-F-]{36}|cbot_\d+)$/;
const CHANNEL_HREF = /^\/o\/([^/]+)\/messages\/(\d+)$/;
const TEAM_HREF = /^\/o\/([^/]+)\/teams\/(\d+)$/;

export function parseInternalReferenceHref(href) {
  if (!href || typeof href !== "string") return null;
  let path = href;
  try {
    if (href.startsWith("http://") || href.startsWith("https://")) {
      path = new URL(href).pathname;
    }
  } catch {
    return null;
  }
  const user = path.match(USER_HREF);
  if (user) {
    return {
      type: "user",
      orgId: decodeURIComponent(user[1]),
      targetId: user[2],
    };
  }
  const team = path.match(TEAM_HREF);
  if (team) {
    return {
      type: "team",
      orgId: decodeURIComponent(team[1]),
      targetId: team[2],
    };
  }
  const channel = path.match(CHANNEL_HREF);
  if (channel) {
    return {
      type: "channel",
      orgId: decodeURIComponent(channel[1]),
      targetId: channel[2],
    };
  }
  return null;
}

export function collectReferencesFromMarkdown(markdown, orgId) {
  if (!markdown) return [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  const out = [];
  const seen = new Set();
  let match;
  while ((match = re.exec(String(markdown))) !== null) {
    const parsed = parseInternalReferenceHref(match[2]);
    if (!parsed) continue;
    if (orgId != null && String(parsed.orgId) !== String(orgId)) continue;
    const key = `${parsed.type}:${parsed.targetId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      type: parsed.type,
      target_id: parsed.targetId,
    });
  }
  return out;
}
